/**
 * useDesignSystem — Fetches design tokens from the CMS and injects
 * them as CSS custom properties on :root.
 *
 * The WordPress admin can change colors, typography, spacing etc.
 * and the frontend automatically reflects those changes without
 * any code modifications.
 */

import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { apiClient } from '../api/client';

interface DesignSystemResponse {
  colors: Record<string, string>;
  darkColors: Record<string, string>;
  typography: Record<string, any>;
  ui: Record<string, string>;
  brand: { logoLight: string; logoDark: string; favicon: string; siteName: string };
  cssVars: Record<string, string>;
}

async function fetchDesignSystem(): Promise<DesignSystemResponse> {
  try {
    const response = await apiClient.get<DesignSystemResponse>('/wp-json/otpl/v1/design-system');
    return response.data;
  } catch {
    // Return defaults if endpoint is unavailable
    return getDefaultDesignSystem();
  }
}

function getDefaultDesignSystem(): DesignSystemResponse {
  return {
    colors: {
      primary: '#2563eb', secondary: '#7c3aed', accent: '#06b6d4',
      success: '#16a34a', warning: '#d97706', error: '#dc2626',
      background: '#ffffff', surface: '#f8fafc', border: '#e2e8f0',
      textPrimary: '#0f172a', textSecondary: '#475569',
      link: '#2563eb', hover: '#1d4ed8',
    },
    darkColors: {
      primary: '#3b82f6', secondary: '#8b5cf6', accent: '#22d3ee',
      success: '#22c55e', warning: '#f59e0b', error: '#ef4444',
      background: '#0f172a', surface: '#1e293b', border: '#334155',
      textPrimary: '#f8fafc', textSecondary: '#94a3b8',
      link: '#60a5fa', hover: '#93c5fd',
    },
    typography: { fontFamily: "'Manrope', system-ui, sans-serif", headingFamily: "'Plus Jakarta Sans', system-ui, sans-serif" },
    ui: {
      borderRadius: '0.5rem', containerWidth: '1280px',
      gridSpacing: '1.5rem', sectionPadding: '5rem',
      animationSpeed: '300ms', transitionDuration: '200ms',
    },
    brand: { logoLight: '', logoDark: '', favicon: '', siteName: 'OTPL Headless CMS' },
    cssVars: {},
  };
}

/**
 * CSS variable names that `globals.css` already defines, as HSL
 * triplets (e.g. `222 47% 11%`, no `hsl()` wrapper), for its own
 * light/dark theme system (`:root` / `.dark` blocks, consumed via
 * `hsl(var(--color-text-primary))` throughout `.rich-content`).
 *
 * The CMS `/design-system` endpoint independently emits *raw hex*
 * values (e.g. `#0f172a`) under some of these exact same names
 * (`colors.textPrimary` → `--color-text-primary`, etc). Setting those
 * as inline styles on `:root` — as this module used to do
 * unconditionally — beats the `.dark` class rule's specificity, so
 * the CMS's light-mode hex value permanently wins even in dark mode.
 * `hsl(#0f172a)` is invalid CSS, so the browser drops the color
 * declaration entirely, leaving text with no explicit color (reads as
 * near-black on the dark background — the "text invisible in dark
 * mode" bug).
 *
 * Skipping these specific names preserves every non-colliding CMS
 * token (`--color-primary`, `--color-secondary`, `--color-background`,
 * `--color-surface`, `--color-link`, `--color-hover`, all `--font-*`
 * and `--ui-*` vars) while leaving the frontend's own light/dark text,
 * border, and status-color system intact.
 */
const RESERVED_THEME_VARS = new Set([
  '--color-text-primary',
  '--color-text-secondary',
  '--color-accent',
  '--color-border',
  '--color-error',
  '--color-warning',
  '--color-success',
]);

/**
 * Injects CSS variables onto :root and handles dark mode.
 */
function applyDesignTokens(data: DesignSystemResponse): void {
  const root = document.documentElement;

  // Apply all CSS vars from the API response, except the reserved
  // names above (see RESERVED_THEME_VARS docblock).
  if (data.cssVars && Object.keys(data.cssVars).length > 0) {
    Object.entries(data.cssVars).forEach(([key, value]) => {
      if (!key.startsWith('--dark-') && !RESERVED_THEME_VARS.has(key)) {
        root.style.setProperty(key, value);
      }
    });
  }

  // Apply dark mode colors to a CSS class rule — same reserved-name
  // exclusion, otherwise the CMS's dark-mode hex values would collide
  // with `.dark`'s own HSL-triplet definitions in globals.css.
  const darkVars = Object.entries(data.cssVars || {})
    .filter(([key]) => key.startsWith('--dark-'))
    .filter(([key]) => !RESERVED_THEME_VARS.has(key.replace('--dark-', '--')))
    .map(([key, value]) => {
      const lightKey = key.replace('--dark-', '--');
      return `${lightKey}: ${value};`;
    })
    .join('\n  ');

  if (darkVars) {
    const styleId = 'design-system-dark';
    let styleEl = document.getElementById(styleId) as HTMLStyleElement | null;
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = `.dark {\n  ${darkVars}\n}`;
  }

  // Apply CMS typography: only font-family and body size
  const typoVars = data.cssVars || {};
  const typoRules: string[] = [];
  if (typoVars['--font-family']) typoRules.push(`body, html { font-family: ${typoVars['--font-family']} !important; }`);
  if (typoVars['--font-heading']) typoRules.push(`h1,h2,h3,h4,h5,h6 { font-family: ${typoVars['--font-heading']} !important; }`);
  if (typoVars['--font-body-size']) typoRules.push(`body { font-size: ${typoVars['--font-body-size']} !important; }`);

  // Derive brand tokens from primary/secondary colors
  const primary = typoVars['--color-primary'];
  const secondary = typoVars['--color-secondary'];
  if (primary) {
    root.style.setProperty('--brand-primary', primary);
    root.style.setProperty('--brand-primary-light', primary);
    root.style.setProperty('--brand-primary-dark', primary);
  }
  if (secondary) {
    root.style.setProperty('--brand-accent', secondary);
    root.style.setProperty('--brand-accent-light', secondary);
  }
  if (primary && secondary) {
    root.style.setProperty('--brand-gradient', `linear-gradient(135deg, ${primary}, ${secondary})`);
    root.style.setProperty('--brand-gradient-text', `linear-gradient(to right, ${primary}, ${secondary})`);
  }
  if (typoRules.length > 0) {
    const typoStyleId = 'design-system-typography';
    let typoEl = document.getElementById(typoStyleId) as HTMLStyleElement | null;
    if (!typoEl) {
      typoEl = document.createElement('style');
      typoEl.id = typoStyleId;
      document.head.appendChild(typoEl);
    }
    typoEl.textContent = typoRules.join('\n');
  }

  // Apply favicon
  if (data.brand?.favicon) {
    let link = document.querySelector('link[rel="icon"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = data.brand.favicon;
  }

  // Apply site title
  if (data.brand?.siteName) {
    const titleEl = document.querySelector('title');
    if (titleEl && !titleEl.dataset.customTitle) {
      // Only set if not already set by a page-specific title
      titleEl.dataset.brand = data.brand.siteName;
    }
  }
}

export function useDesignSystem() {
  const query = useQuery<DesignSystemResponse>({
    queryKey: ['design-system'],
    queryFn: fetchDesignSystem,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  // Inject CSS vars whenever data changes
  useEffect(() => {
    if (query.data) {
      applyDesignTokens(query.data);
    }
  }, [query.data]);

  return query;
}
